/* eslint-disable no-undefined */
/* eslint-disable no-console */
import { useState, useEffect } from "react";
import InputGroup from "../../../components/FormComponent/InputGroup";
import { httprequest } from "../../../data/api";
import { toast } from "react-toastify";
import BreadCrumb from "../../../components/BreadCrumb";
import Layout from "../../../components/Layout";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import {
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper
} from "@mui/material";
import useStyles from "../styles";
import Button from "../../../components/Button";
import TextAreaGroup from "../../../components/FormComponent/TextAreaGroup";
import { useNavigate, useParams } from "react-router-dom";
import NoData from "../../dashboard/components/NotFoundCard/NoData";
import nosearch from "../../../assets/images/no-search.svg";
import PageLoader from "../../../components/PageLoader";

const TERM_ENDPOINT = "/api/v1/school-plan/terms";

const SESSION_ENDPOINT = "/api/v1/school-plan/session";
const ADMIN_CLASS_ENDPOINT = "/api/v1/class";
const REPORT_ENDPOINT = "/api/v1/custom-reports";
const GRADING_SYSTEM = "/api/v1/grading-system";
const EXTRAS_ENDPOINT = "/api/v1/school-extras";

const EditReport = () => {
  const classes = useStyles();
  const { id, studentid } = useParams();
  const navigate = useNavigate();
  const [allTerm, setAllTerms] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [allGradingSystem, setAllGradingSystem] = useState([]);
  const [formData, setFormData] = useState({
    student_name: "",
    session: "",
    term_id: "",
    class_id: "",
    student_type: "",
    results: [],
    extras: [],
    activities: [],
    teacher_comment: ""
  });
  const [isPageFetching, setIsPageFetching] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [caColumns, setCaColumns] = useState([]);
  const [extraData, setExtrasData] = useState([]);

  const fetchClasses = () => {
    httprequest
      .get(ADMIN_CLASS_ENDPOINT)
      .then((res) => {
        setClassData(res?.data?.data);
      })
      .catch(() => {
      });
  };

  const getAllSession = () => {
    httprequest
      .get(SESSION_ENDPOINT)
      .then((res) => {
        setSessionData(res?.data?.data);
      })
      .catch(() => {});
  };

  const getAllTerms = (Session_id) => {
    httprequest
      .get(TERM_ENDPOINT)
      .then((res) => {
        setAllTerms(res?.data?.data.filter((session_term) => session_term?.session_id === Session_id));
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  const handleGetAllGradingSystem = () => {
    httprequest.get(GRADING_SYSTEM).then((res) => {
      setAllGradingSystem(res?.data?.data);
    }).catch((err) => {
      toast.error(err?.response?.data?.message);
    });
  };

  const handleEditSessionChange = (e) => {
    const { value } = e.target;
    getAllTerms(value);
  };

  const getAllExtras = () => {
    httprequest
      .get(EXTRAS_ENDPOINT)
      .then((res) => {
        setExtrasData(res.data.data);

        if (res.data.data && res.data.data.extras) {
          const defaultExtras = res.data.data.extras.map((extra) => ({
            section_name: extra.section_name,
            section_type: extra.section_type,
            section_value: (extra.section_titles || []).reduce((acc, title) => {
              acc[title] = "";

              return acc;
            }, {})
          }));

          setFormData(prev => ({
            ...prev,
            extras: prev.extras.length > 0 ? prev.extras : defaultExtras
          }));
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data);
      });
  };

  const fetchReportData = async () => {
    setIsPageFetching(true);

    try {
      let response;

      if (studentid) {
        response = await httprequest.get(`/api/v1/custom-reports/${id}/${studentid}/combined`);
      } else {
        response = await httprequest.get(`${REPORT_ENDPOINT}/student/${id}`);
      }

      const report = response?.data?.data;

      if (!report) {
        throw new Error("No report data received");
      }

      // Extract CA columns from the result data
      const extractCAColumns = (results) => {
        const columns = new Set();
        results?.forEach(subject => {
          if (subject.CA_scores) {
            Object.keys(subject.CA_scores).forEach(ca => columns.add(ca));
          }
        });

        return Array.from(columns);
      };

      const extractedCaColumns = extractCAColumns(report.result);
      setCaColumns(extractedCaColumns);

      // Extract activities from extras
      const activities = [];
      (report.extras || []).forEach(section => {
        if (section.section_type === "grade" || section.section_type === "score") {
          Object.entries(section.section_value || {}).forEach(([activity, value]) => {
            activities.push({
              activity,
              value,
              type: section.section_type,
              sectionName: section.section_name
            });
          });
        }
      });

      setFormData(prev => ({
        ...prev,
        student_name: `${report?.student?.first_name} ${report?.student?.last_name}`,
        session: report.session?.id || "",
        term_id: report?.term?.id || "",
        class_id: report?.class?.id || "",
        student_type: "",
        results: report?.result || [],
        extras: report?.extras?.length > 0 ? report.extras : prev.extras,
        activities,
        teacher_comment: (report?.extras || []).find(e => e.section_name === "teacher's comment")?.section_value || ""
      }));
      setReportData(report);

      if (report.session?.id) {
        getAllTerms(report.session.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load report data");
    } finally {
      setIsPageFetching(false);
    }
  };

  const handleActivityChange = (sectionName, originalActivityKey, field, value) => {
    if (field === "activity") {
      setFormData(prev => {
        const updatedExtras = prev.extras.map(section => {
          if (section.section_name === sectionName) {
            const newSectionValue = { ...section.section_value };
            if (newSectionValue[originalActivityKey] !== undefined) {
              newSectionValue[value] = newSectionValue[originalActivityKey];
              delete newSectionValue[originalActivityKey];
            }

            return {
              ...section,
              section_value: newSectionValue
            };
          }

          return section;
        });

        const updatedActivities = prev.activities.map(activity => {
          if (activity.activity === originalActivityKey && activity.sectionName === sectionName) {
            return {
              ...activity,
              activity: value
            };
          }

          return activity;
        });

        return {
          ...prev,
          extras: updatedExtras,
          activities: updatedActivities
        };
      });
    } else {
      setFormData(prev => {
        const updatedActivities = [...prev.activities];
        const activityIndex = updatedActivities.findIndex(
          a => a.activity === originalActivityKey && a.sectionName === sectionName
        );

        if (activityIndex !== -1) {
          updatedActivities[activityIndex] = {
            ...updatedActivities[activityIndex],
            [field]: value
          };
        } else {
          updatedActivities.push({
            activity: originalActivityKey,
            [field]: value,
            type: field === "value" && !isNaN(value) ? "score" : "grade",
            sectionName
          });
        }

        return {
          ...prev,
          activities: updatedActivities
        };
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      let transformedExtras = [];

      if (formData.extras && formData.extras.length > 0) {
        transformedExtras = [...formData.extras];
      } else {
        transformedExtras = (extraData?.extras || []).map(extra => ({
          section_name: extra.section_name,
          section_type: extra.section_type,
          section_value: (extra.section_titles || []).reduce((acc, title) => {
            acc[title] = "";

            return acc;
          }, {})
        }));
      }

      const gradeActivities = formData.activities
        .filter(activity => activity.type === "grade")
        .reduce((acc, curr) => {
          acc[curr.activity] = curr.value;

          return acc;
        }, {});

      const scoreActivities = formData.activities
        .filter(activity => activity.type === "score")
        .reduce((acc, curr) => {
          acc[curr.activity] = Number(curr.value);

          return acc;
        }, {});

      transformedExtras = transformedExtras.map(section => {
        if (section.section_type === "grade") {
          const updatedSectionValue = { ...section.section_value };
          Object.keys(gradeActivities).forEach(activity => {
            updatedSectionValue[activity] = gradeActivities[activity];
          });

          return {
            ...section,
            section_value: updatedSectionValue
          };
        } else if (section.section_type === "score") {
          const updatedSectionValue = { ...section.section_value };
          Object.keys(scoreActivities).forEach(activity => {
            updatedSectionValue[activity] = scoreActivities[activity];
          });

          return {
            ...section,
            section_value: updatedSectionValue
          };
        } else if (section.section_name === "teacher's comment") {
          return {
            ...section,
            section_value: formData.teacher_comment || ""
          };
        }

        return section;
      });

      const hasTeacherComment = transformedExtras.some(section => section.section_name === "teacher's comment"
      );

      if (!hasTeacherComment) {
        transformedExtras.push({
          section_name: "teacher's comment",
          section_type: "text",
          section_value: formData.teacher_comment || ""
        });
      }

      if (transformedExtras.length === 0) {
        transformedExtras = (extraData?.extras || []).map(extra => ({
          section_name: extra.section_name,
          section_type: extra.section_type,
          section_value: (extra.section_titles || []).reduce((acc, title) => {
            acc[title] = "";

            return acc;
          }, {})
        }));

        transformedExtras.push({
          section_name: "teacher's comment",
          section_type: "text",
          section_value: formData.teacher_comment || ""
        });
      }

      const payload = {
        extras: transformedExtras

      };

      console.log("Saving payload:", JSON.stringify(payload, null, 2));

      const response = await httprequest.put(`${REPORT_ENDPOINT}/${studentid}/${id}`, payload);

      toast.success(response.data.message || "Report saved successfully");
      setTimeout(() => {
        navigate("/report/custom");
      }, 1300);
    } catch (error) {
      console.error("Save error:", error.response?.data || error);
      toast.error(error.response?.data?.message || "Failed to save report");
    }
  };

  const handlePublish = async () => {
    try {
      const finalResponse = await httprequest.patch(`${REPORT_ENDPOINT}/ready-for-publish`, {
        report_ids: [id]
      });

      toast.success(finalResponse.data.message || "Report published and uploaded successfully");
      setTimeout(() => {
        navigate("/report/custom");
      }, 1300);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to publish report");
    }
  };

  useEffect(() => {
    fetchReportData();
    fetchClasses();
    getAllSession();
    handleGetAllGradingSystem();
    getAllExtras();
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Layout children={
      <>
        <BreadCrumb
          link_one="/report/custom"
          link_one_name="Custom Reports"
          active="View Custom Report"
          description="View students reports here."
        />

        {isPageFetching ? (
          <PageLoader />
        ) : (
          <>
            {!reportData ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }} className="page-content container-border">
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <NoData
                    title="Report Unavailable"
                    subtitle="No Result uploaded or is currently being processed"
                    img={nosearch}
                  />
                  <Button
                    variant="primary"
                    buttonSize="fluid"
                    color="btndefault"
                    buttonName="Return To Custom Report"
                    onClick={handleGoBack}
                  />
                </div>
              </div>
            ) : (
              <div className="page-content margin-top-30">
                <Grid container spacing={2} className="input-padding">
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <InputGroup
                      inputType="text"
                      label="Student Name"
                      inputName="student_name"
                      value={formData.student_name}
                      disabled
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectGroup
                      label="Select Session"
                      required
                      children={
                        <select
                          name="session"
                          value={formData.session}
                          onChange={handleEditSessionChange}
                          disabled
                        >
                          <option value="">---Select---</option>
                          {sessionData?.map((sesh) => {
                            return (
                              <option key={sesh?.id} value={sesh?.id}>
                                {sesh?.session}
                              </option>
                            );
                          })}
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectGroup
                      label="Select Term"
                      required
                      disabled
                      children={
                        <select
                          name="term_id"
                          value={formData.term_id}
                          onChange={handleInputChange}
                        >
                          <option value="">--- Select ---</option>
                          {allTerm?.map((term) => (
                            <option key={term?.id} value={term?.id}>
                              {term?.term}
                            </option>
                          ))}
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectGroup
                      label="Select Class"
                      required
                      children={
                        <select
                          name="class_id"
                          value={formData.class_id}
                          onChange={handleInputChange}
                          disabled
                        >
                          <option value="">--- Select ---</option>
                          {classData?.map((clss) => (
                            <option key={clss?.id} value={clss?.id}>
                              {clss?.class_name}
                            </option>
                          ))}
                        </select>
                      }
                    />
                  </Grid>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectGroup
                      label="Student Type"
                      required
                      disabled
                      children={
                        <select
                          name="student_type"
                        >
                          <option value="">--- Select ---</option>
                        </select>
                      }
                    />
                  </Grid>
                </Grid>

                {/* Subject Results Table */}
                <div className="margin-top-16">
                  <p className="margin-top-16" style={{ textAlign: "left", width: "fit-content", fontWeight: "bold" }}>Subject Results</p>
                  <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.subjectHeader}>Subject</TableCell>
                          {caColumns?.map((ca) => (
                            <TableCell className={classes.headerCell} key={ca} align="center">{ca}</TableCell>
                          ))}
                          <TableCell align="center">Total</TableCell>
                          <TableCell className={classes.headerCell} align="center">Grade</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.results?.map((subject, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{
                              position: "sticky",
                              left: 0,
                              backgroundColor: "#fff",
                              zIndex: 1
                            }}
                            >
                              {subject.subject_name}
                            </TableCell>
                            {caColumns?.map((ca) => (
                              <TableCell key={ca} align="center">
                                {subject.CA_scores?.[ca] || "-"}
                              </TableCell>
                            ))}
                            <TableCell align="center">{subject.total_score}</TableCell>
                            <TableCell align="center">{subject.grade}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>

                {/* Extracurriculars Section */}
                <div className={classes.extra}>
                  <p style={{ textAlign: "left", width: "fit-content", fontWeight: "bold" }}>Extracurriculars</p>
                  {(formData.extras.length > 0 ? formData.extras : (extraData?.extras || []))
                    .filter(section => section.section_type === "grade")
                    .map((section, sectionIndex) => (
                      <div key={`grade-section-${sectionIndex}`} style={{ marginBottom: "32px" }}>
                        <div className="flex gap-10" style={{ alignItems: "center", marginBottom: "16px" }}>
                          <p className={classes.extrahead}>{section.section_name.toUpperCase()}</p>
                          <p className={classes.extrahead}>Grade</p>
                        </div>

                        {Object.entries(section.section_value).map(([activity, grade], activityIndex) => (
                          <Grid container spacing={2} key={`grade-activity-${activityIndex}`} style={{ marginBottom: "16px", alignItems: "center" }}>
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                              <InputGroup
                                inputName="name"
                                inputType="text"
                                value={activity}
                                disabled
                                onChange={(e) => handleActivityChange(
                                  section.section_name,
                                  activity,
                                  "activity",
                                  e.target.value
                                )}
                              />
                            </Grid>
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                              <SelectGroup
                                children={
                                  <select
                                    value={formData.activities.find(a => a.activity === activity &&
                                      a.sectionName === section.section_name
                                    )?.value || grade || ""}
                                    onChange={(e) => {
                                      const selectedGrade = e.target.value;
                                      handleActivityChange(
                                        section.section_name,
                                        activity,
                                        "value",
                                        selectedGrade
                                      );
                                    }}
                                  >
                                    <option value="">--- Select Grade ---</option>
                                    {(allGradingSystem[0]?.grading || []).map((grade_option, idx) => (
                                      <option key={idx} value={grade_option.grade}>
                                        {grade_option.grade}
                                      </option>
                                    ))}
                                  </select>
                                }
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </div>
                    ))}

                  {(formData.extras.length > 0 ? formData.extras : (extraData?.extras || []))
                    .filter(section => section.section_type === "score")
                    .map((section, sectionIndex) => (
                      <div key={`score-section-${sectionIndex}`} style={{ marginBottom: "32px" }}>
                        <div className="flex gap-10" style={{ alignItems: "center", marginBottom: "16px" }}>
                          <p className={classes.extrahead}>{section.section_name.toUpperCase()}</p>
                          <p className={classes.extrahead}>Score</p>
                        </div>

                        {Object.entries(section.section_value).map(([activity, score], activityIndex) => (
                          <Grid container spacing={2} key={`score-activity-${activityIndex}`} style={{ marginBottom: "16px", alignItems: "center" }}>
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                              <InputGroup
                                inputType="text"
                                disabled
                                value={activity}
                                onChange={(e) => handleActivityChange(
                                  section.section_name,
                                  activity,
                                  "activity",
                                  e.target.value
                                )}
                              />
                            </Grid>
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                              <InputGroup
                                inputType="number"
                                inputName="score"
                                value={formData.activities.find(a => a.activity === activity &&
                                  a.sectionName === section.section_name
                                )?.value || score || ""}
                                onChange={(e) => handleActivityChange(
                                  section.section_name,
                                  activity,
                                  "value",
                                  e.target.value
                                )}
                                min="0"
                                max="100"
                              />
                            </Grid>
                          </Grid>
                        ))}
                      </div>
                    ))}
                </div>

                {/* Teacher's Comment */}
                <Grid container spacing={2}>
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <p style={{ textAlign: "left", width: "fit-content", margin: 0 }}>Teacher's Comment</p>
                    <TextAreaGroup
                      inputName="teacher_comment"
                      value={formData.teacher_comment}
                      margin="normal"
                      placeholder="Enter any additional information you would like to send."
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>

                {/* Action Buttons */}
                <div className="flex gap-10 margin-top-30">
                  <Button
                    variant="dangeroutline"
                    buttonSize="fluid"
                    color="btndanger"
                    buttonName="Cancel"
                    onClick={handleGoBack}
                  />
                  <Button
                    variant="primaryoutline"
                    buttonSize="fluid"
                    color="btnfontprimary"
                    buttonName="Save"
                    onClick={handleSave}
                  />
                  <Button
                    variant="primary"
                    buttonSize="fluid"
                    color="btndefault"
                    buttonName="Ready to publish"
                    onClick={handlePublish}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* <ToastContainer /> */}
      </>
    }
    />
  );
};

export default EditReport;
